import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Users,
    UserPlus,
    Loader2,
    Trash2,
    Crown,
    X,
    LogOut as LeaveIcon,
} from "lucide-react";
import TopNav from "../components/TopNav";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useAuth, formatApiError } from "../lib/auth";
import {
    listWorkgroups,
    listWorkgroupMembers,
    createWorkgroup,
    sendWorkgroupInvite,
    listWorkgroupInvites,
    cancelWorkgroupInvite,
    removeWorkgroupMember,
    deleteWorkgroup,
} from "../lib/api";

/**
 * iter-120: Workgroup settings moved back into the user dropdown menu
 * (directly under Account). Standalone page instead of an inline card
 * on the Account page — gives more room for member list + invite
 * management. Route is `/workgroup`.
 */
export default function WorkgroupSettingsPage() {
    const { user } = useAuth();
    const [workgroup, setWorkgroup] = useState(null);
    const [members, setMembers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviting, setInviting] = useState(false);
    const [newName, setNewName] = useState("");
    const [creating, setCreating] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const wgs = await listWorkgroups();
            if (wgs && wgs.length > 0) {
                const wg = wgs[0];
                setWorkgroup(wg);
                const [mems, invs] = await Promise.all([
                    listWorkgroupMembers(wg.id).catch(() => []),
                    listWorkgroupInvites(wg.id).catch(() => []),
                ]);
                setMembers(mems || []);
                setInvites((invs || []).filter((i) => i.status === "pending"));
            } else {
                setWorkgroup(null);
                setMembers([]);
                setInvites([]);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        load();
    }, [load]);

    const isOwner = workgroup && workgroup.created_by_user_id === user?.user_id;

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newName.trim()) return;
        setCreating(true);
        try {
            await createWorkgroup(newName.trim());
            toast.success("Workgroup created — your existing projects, contacts, leads, and reports are now shared with any teammate you invite.");
            setNewName("");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setCreating(false);
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        const email = inviteEmail.trim();
        if (!email || !email.includes("@")) {
            toast.error("Enter a valid email.");
            return;
        }
        setInviting(true);
        try {
            await sendWorkgroupInvite(workgroup.id, email);
            toast.success(`Invite sent to ${email}`);
            setInviteEmail("");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setInviting(false);
        }
    };

    const handleCancelInvite = async (inviteId) => {
        if (!window.confirm("Cancel this invite?")) return;
        setBusy(true);
        try {
            await cancelWorkgroupInvite(workgroup.id, inviteId);
            toast.success("Invite cancelled.");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const handleKick = async (memberId, memberName) => {
        if (!window.confirm(`Remove ${memberName || "this member"}? Everything they own inside the workgroup will be transferred to you (the owner). They'll start over with an empty account.`)) return;
        setBusy(true);
        try {
            await removeWorkgroupMember(workgroup.id, memberId);
            toast.success(`${memberName || "Member"} removed.`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const handleLeave = async () => {
        const myMember = members.find((m) => m.user_id === user.user_id);
        if (!myMember) return;
        if (!window.confirm("Leave this workgroup? EVERYTHING you contributed will stay with the workgroup owner — you'll start fresh with an empty account. This can't be undone.")) return;
        setBusy(true);
        try {
            await removeWorkgroupMember(workgroup.id, myMember.id);
            toast.success("You left the workgroup.");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Delete the entire workgroup? Every member will be kicked out and lose access to the shared items. This can't be undone.")) return;
        setBusy(true);
        try {
            await deleteWorkgroup(workgroup.id);
            toast.success("Workgroup deleted.");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        // iter-131d: App-wide index.css pins body to `position:fixed;
        // overflow:hidden` (kept there to stop the iOS URL-bar jump),
        // so every page needs its OWN internal scroll region. Previous
        // `min-h-screen` + plain <main> layout meant the page silently
        // could not scroll and the Delete-workgroup button was hidden
        // behind the fixed BottomToolbar. Match the pattern used by
        // /account + /contacts: full-height flex column, main owns the
        // scroll via `flex-1 min-h-0 overflow-y-auto`.
        <div
            className="h-screen bg-[#0A1128] blueprint-bg-soft flex flex-col overflow-hidden"
            data-testid="workgroup-settings-page"
        >
            <TopNav />
            <main className="flex-1 min-h-0 overflow-y-auto">
                <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-10 pb-32">
                <div className="mb-6">
                    <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                        <Users className="w-6 h-6 text-[#00A3FF]" />
                        Workgroup
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        {workgroup
                            ? "Everything you own is auto-shared with every member. Invite teammates and they'll instantly see all your projects, contacts, leads, and reports — with full edit access."
                            : "Create a workgroup to share every project, contact, lead, and report with teammates automatically. You can only belong to ONE workgroup at a time."}
                    </p>
                </div>

                {loading ? (
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                ) : !workgroup ? (
                    <section
                        className="rounded-lg border border-[#2A3655] bg-[#0F1B3A]/80 p-5 md:p-6"
                        data-testid="wg-empty-state"
                    >
                        <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-2">
                            <Input
                                placeholder="Workgroup name (e.g. Acme Contracting)"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                className="flex-1 bg-[#0A1128] border-[#2A3655] text-white"
                                data-testid="wg-create-name"
                            />
                            <Button
                                type="submit"
                                disabled={creating || !newName.trim()}
                                className="bg-[#00A3FF] hover:bg-[#0088dd]"
                                data-testid="wg-create-submit"
                            >
                                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create workgroup"}
                            </Button>
                        </form>
                    </section>
                ) : (
                    <section
                        className="rounded-lg border border-[#2A3655] bg-[#0F1B3A]/80 p-5 md:p-6 space-y-6"
                        data-testid="wg-detail"
                    >
                        <div className="flex items-center gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="text-xl font-bold text-white truncate">{workgroup.name}</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    Created {workgroup.created_at ? new Date(workgroup.created_at).toLocaleDateString() : "—"}
                                </div>
                            </div>
                        </div>

                        {/* Members */}
                        <div>
                            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">
                                Members ({members.length})
                            </div>
                            <div className="rounded-sm border border-[#2A3655] bg-[#0A1128] divide-y divide-[#2A3655]">
                                {members.map((m) => {
                                    const isCreator = m.user_id === workgroup.created_by_user_id;
                                    const isMe = m.user_id === user.user_id;
                                    return (
                                        <div
                                            key={m.id}
                                            className="flex items-center gap-3 px-3 py-2.5"
                                            data-testid={`wg-member-${m.user_id}`}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm text-white truncate flex items-center gap-2">
                                                    {m.name || m.email || m.user_id}
                                                    {isMe && <span className="text-[10px] text-slate-500">(you)</span>}
                                                    {isCreator && (
                                                        <Crown className="w-3 h-3 text-amber-400" />
                                                    )}
                                                </div>
                                                <div className="text-[11px] text-slate-500 truncate">{m.email}</div>
                                            </div>
                                            {isOwner && !isCreator && (
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => handleKick(m.id, m.name || m.email)}
                                                    disabled={busy}
                                                    className="h-7 px-2 text-[11px] text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                                                    data-testid={`wg-kick-${m.user_id}`}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {isOwner && (
                            <div>
                                <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">
                                    Invite a teammate
                                </div>
                                <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-2">
                                    <Input
                                        type="email"
                                        placeholder="teammate@company.com"
                                        value={inviteEmail}
                                        onChange={(e) => setInviteEmail(e.target.value)}
                                        className="flex-1 bg-[#0A1128] border-[#2A3655] text-white"
                                        data-testid="wg-invite-email"
                                    />
                                    <Button
                                        type="submit"
                                        disabled={inviting || !inviteEmail.trim()}
                                        className="bg-[#00A3FF] hover:bg-[#0088dd]"
                                        data-testid="wg-invite-submit"
                                    >
                                        {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1" /> Invite</>}
                                    </Button>
                                </form>
                                {invites.length > 0 && (
                                    <div className="mt-3 space-y-1">
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                                            Pending
                                        </div>
                                        {invites.map((inv) => (
                                            <div
                                                key={inv.id}
                                                className="flex items-center gap-2 px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm text-xs"
                                            >
                                                <span className="flex-1 text-slate-300 truncate">{inv.invited_email}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleCancelInvite(inv.id)}
                                                    disabled={busy}
                                                    className="text-slate-500 hover:text-rose-400"
                                                    data-testid={`wg-cancel-invite-${inv.id}`}
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="pt-3 border-t border-[#2A3655] flex flex-wrap gap-2">
                            {isOwner ? (
                                <Button
                                    type="button"
                                    variant="destructive"
                                    onClick={handleDelete}
                                    disabled={busy}
                                    className="text-xs"
                                    data-testid="wg-delete"
                                >
                                    <Trash2 className="w-3 h-3 mr-1" /> Delete workgroup
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    variant="destructive"
                                    onClick={handleLeave}
                                    disabled={busy}
                                    className="text-xs"
                                    data-testid="wg-leave"
                                >
                                    <LeaveIcon className="w-3 h-3 mr-1" /> Leave workgroup
                                </Button>
                            )}
                        </div>
                    </section>
                )}
                </div>
            </main>
        </div>
    );
}
